import { useQuery } from '@tanstack/react-query'
import { Award, QrCode, ScrollText, Users } from 'lucide-react'
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Panel, Section, Stat } from './Section'
import { SDG } from '../../components/flags'
import { cn } from '../../lib/cn'
import { get } from '../../lib/api'
import type { AdminDashboard, BoothTraffic } from './types'

/** Slide 23's dashboard: headline figures, charts, booth traffic and the live floor feed. */

interface RecentScan {
  scannedAt: string
  method: 'qr' | 'manual'
  boothNo: number
  school: string
  name: string
  regNo: string | null
  /** Null for an attendee from another institution; `regNo` identifies them. */
  studentId: string | null
  boothsVisited: number
}

function ChartTip({ active, payload }: { active?: boolean; payload?: { payload: { tip: string; v: number } }[] }) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-lg">
      <p className="font-bold text-[#0B1E3F]">{p.tip}</p>
      <p className="tabular-nums text-slate-500">{p.v.toLocaleString()}</p>
    </div>
  )
}

export function DashboardSection({ live }: { live: { data: AdminDashboard | null; state: string } }) {
  const o = live.data?.overview

  // Keyed on the scan count so these refetch when a scan lands, not on a timer.
  const scanCount = o?.booths.scans ?? 0

  const booths = useQuery({
    queryKey: ['admin-booths', scanCount],
    queryFn: () => get<{ booths: BoothTraffic[] }>('/api/admin/booths', 'staff'),
    enabled: !!o,
    staleTime: 5_000,
  })

  const feed = useQuery({
    queryKey: ['admin-recent', scanCount],
    queryFn: () => get<{ scans: RecentScan[] }>('/api/admin/scans/recent?limit=12', 'staff'),
    enabled: !!o,
    staleTime: 5_000,
  })

  if (!o) {
    return (
      <Section title="Dashboard">
        <div className="grid place-items-center gap-3 py-24">
          <span className="size-8 animate-spin rounded-full border-[3px] border-slate-200 border-t-orange-500" />
          <p className="text-sm text-slate-500">
            {live.state === 'error' ? 'Reconnecting to the live feed…' : 'Connecting…'}
          </p>
        </div>
      </Section>
    )
  }

  const j = o.judging
  const judgingPie = [
    { name: 'Submitted', v: j.submitted, tip: 'Submitted', c: '#10B981' },
    { name: 'In progress', v: j.inProgress, tip: 'In progress', c: '#F59E0B' },
    { name: 'Not started', v: j.notStarted, tip: 'Not started', c: '#E2E8F0' },
  ]

  const funnel = [
    { name: 'Registered', v: o.students.registered, tip: 'Registered', c: '#3B82F6' },
    { name: 'Verified', v: o.students.verified, tip: 'Verified e-mail', c: '#06B6D4' },
    { name: `${o.rules.luckyDrawThreshold}+ booths`, v: o.students.drawEligible, tip: 'In the lucky draw', c: '#F59E0B' },
    { name: `All ${o.rules.totalBooths}`, v: o.students.completed, tip: 'Finished every booth', c: '#10B981' },
    { name: 'Certificates', v: o.certificates.issued, tip: 'Certificates issued', c: '#8B5CF6' },
  ]

  const traffic = (booths.data?.booths ?? []).map((b) => ({
    name: String(b.boothNo),
    v: b.scans,
    tip: `Booth ${b.boothNo} · ${b.school}`,
  }))

  const scans = feed.data?.scans ?? []

  return (
    <Section title="Dashboard" description="Updates the moment something happens. Nothing here runs on a timer.">
      <div className="space-y-3">
        {/* Headline figures */}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat
            label="Registered"
            value={o.students.registered.toLocaleString()}
            sub={o.students.unverified > 0 ? `${o.students.unverified} not yet verified` : 'all verified by e-mail'}
            icon={Users}
            color="blue"
          />
          <Stat
            label="Booth scans"
            value={o.booths.scans.toLocaleString()}
            sub={`${o.booths.last15Minutes} in the last 15 minutes`}
            icon={QrCode}
            color="orange"
          />
          <Stat
            label="Certificates sent"
            value={o.certificates.emailed.toLocaleString()}
            sub={
              o.certificates.pending > 0
                ? `${o.certificates.pending} issued but not delivered`
                : `${o.students.completed} finished all ${o.rules.totalBooths} booths`
            }
            attention={o.certificates.pending > 0}
            icon={Award}
            color="violet"
          />
          <Stat
            label="Score sheets in"
            value={`${j.submitted} / ${j.total}`}
            sub={j.allSubmitted ? 'complete: winners can be published' : `${j.inProgress} in progress`}
            icon={ScrollText}
            color="green"
          />
        </div>

        {/* Charts */}
        <div className="grid gap-3 lg:grid-cols-5">
          <Panel title="Student journey" description="From sign-up to certificate" className="lg:col-span-3">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnel} layout="vertical" margin={{ left: 0, right: 28, top: 0, bottom: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={92}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12, fill: '#64748B' }}
                  />
                  <Tooltip content={<ChartTip />} cursor={{ fill: '#F1F5F9' }} />
                  <Bar
                    dataKey="v"
                    radius={[0, 8, 8, 0]}
                    barSize={22}
                    isAnimationActive={false}
                    label={{ position: 'right', fontSize: 12, fill: '#0B1E3F', fontWeight: 700 }}
                  >
                    {funnel.map((d) => (
                      <Cell key={d.name} fill={d.c} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Judging progress" description="Score sheets" className="lg:col-span-2">
            <div className="flex items-center gap-4">
              <div className="relative size-40 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={judgingPie}
                      dataKey="v"
                      innerRadius={50}
                      outerRadius={72}
                      startAngle={90}
                      endAngle={-270}
                      stroke="none"
                      isAnimationActive={false}
                    >
                      {judgingPie.map((d) => (
                        <Cell key={d.name} fill={d.c} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 grid place-items-center text-center">
                  <div className="leading-none">
                    <p className="text-2xl font-black tabular-nums text-[#0B1E3F]">
                      {j.total ? Math.round((j.submitted / j.total) * 100) : 0}%
                    </p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">done</p>
                  </div>
                </div>
              </div>
              <ul className="min-w-0 flex-1 space-y-2 text-sm">
                {judgingPie.map((d) => (
                  <li key={d.name} className="flex items-center gap-2">
                    <span className="size-2.5 shrink-0 rounded-full" style={{ background: d.c }} />
                    <span className="flex-1 text-slate-500">{d.name}</span>
                    <span className="font-bold tabular-nums text-[#0B1E3F]">{d.v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Panel>
        </div>

        <div className="grid gap-3 xl:grid-cols-2">
          {/* Booth traffic */}
          <Panel
            title="Booth traffic"
            description={`${o.booths.scans.toLocaleString()} scans across ${o.rules.totalBooths} booths`}
          >
            {traffic.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">No scans yet.</p>
            ) : (
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={traffic} margin={{ left: -18, right: 4, top: 8, bottom: 0 }}>
                    <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748B' }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
                    <Tooltip content={<ChartTip />} cursor={{ fill: '#F1F5F9' }} />
                    <Bar dataKey="v" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                      {traffic.map((d, i) => (
                        <Cell key={d.name} fill={SDG[i % SDG.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          {/* Live feed */}
          <Panel
            title="Latest scans"
            description="Newest first"
            bodyClassName="p-0"
            actions={
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-600">
                <span className="relative flex size-2">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none" />
                  <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                </span>
                LIVE
              </span>
            }
          >
            {scans.length === 0 ? (
              <p className="px-5 py-12 text-center text-sm text-slate-400">
                No scans yet. They appear here the moment a booth marks someone.
              </p>
            ) : (
              <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto">
                {scans.map((s, i) => (
                  <li key={`${s.scannedAt}-${s.studentId}-${i}`} className="flex items-center gap-3 px-4 py-2.5 sm:px-5">
                    <span className="grid size-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#2B3E9A] to-[#141F55] text-xs font-bold tabular-nums text-white">
                      {s.boothNo}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-[#0B1E3F]">{s.name}</span>
                      <span className="block truncate text-xs text-slate-500">
                        {s.regNo ?? s.studentId} · {s.school}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span
                        className={cn(
                          'block text-sm font-bold tabular-nums',
                          s.boothsVisited >= o.rules.totalBooths ? 'text-emerald-600' : 'text-slate-500',
                        )}
                      >
                        {s.boothsVisited}/{o.rules.totalBooths}
                      </span>
                      <span className="block text-[11px] text-slate-400">
                        {new Date(s.scannedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        {/* Student progress */}
        <Panel title="Student progress">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { label: `Visited ${o.rules.luckyDrawThreshold}+ booths`, value: o.students.drawEligible, note: 'in the lucky draw', c: 'text-amber-600' },
              { label: `Finished all ${o.rules.totalBooths}`, value: o.students.completed, note: 'earned a certificate', c: 'text-emerald-600' },
              { label: 'Certificates issued', value: o.certificates.issued, note: `${o.certificates.emailed} delivered`, c: 'text-violet-600' },
              { label: 'Draw winners', value: o.draws.winners, note: 'across all three draws', c: 'text-orange-600' },
            ].map((item) => (
              <div key={item.label} className="rounded-xl bg-slate-50 p-3.5">
                <dt className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400">{item.label}</dt>
                <dd className={cn('mt-1.5 text-2xl font-black tabular-nums', item.c)}>
                  {item.value.toLocaleString()}
                </dd>
                <dd className="text-xs text-slate-500">{item.note}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      </div>
    </Section>
  )
}
